import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Lock, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const MIN_LENGTH = 8;

// Belt-and-suspenders sign out: calls Supabase signOut with a short timeout,
// then wipes any sb-*-auth-token keys from localStorage so the next attempt
// can't see a stale session. Never throws.
async function forceLocalSignOut() {
  try {
    await Promise.race([
      supabase.auth.signOut(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('signout-timeout')), 1500)),
    ]);
  } catch { /* ignore — manual cleanup below */ }
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('sb-') && k.endsWith('-auth-token')) localStorage.removeItem(k);
    });
  } catch { /* localStorage blocked, fine */ }
}

const SESSION_DEAD_RE = /auth session missing|jwt|not authenticated|invalid refresh token|refresh token not found|session_not_found|user from sub claim/i;

const ForcePasswordResetPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshProfile } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Solo se marca error una vez que el usuario escribió algo en "Confirmar" —
  // evita mostrar "no coinciden" mientras ese campo todavía está vacío.
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleDeadSession = async () => {
    await forceLocalSignOut();
    toast({
      title: 'Sesión expirada',
      description: 'Tu sesión ha caducado. Inicia sesión nuevamente para cambiar la contraseña.',
      variant: 'destructive',
    });
    navigate('/auth', { replace: true });
  };

  // Verify the session is actually valid on mount — getSession() only reads
  // localStorage; getUser() hits the server and tells us if the JWT is alive.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (cancelled) return;
      if (error || !data?.user) {
        await handleDeadSession();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < MIN_LENGTH) {
      toast({ title: 'Contraseña demasiado corta', description: `Debe tener al menos ${MIN_LENGTH} caracteres.`, variant: 'destructive' });
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    setIsLoading(true);

    // Server-side session validation (not just localStorage). If the JWT is
    // dead, bail out immediately instead of letting updateUser error obscurely.
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      setIsLoading(false);
      await handleDeadSession();
      return;
    }

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setIsLoading(false);
      const raw = error.message || '';
      if (SESSION_DEAD_RE.test(raw)) {
        await handleDeadSession();
        return;
      }
      const low = raw.toLowerCase();
      let description = raw;
      if (low.includes('weak') || low.includes('pwned') || low.includes('known to be')) {
        description = 'La contraseña es demasiado común o ha sido filtrada en brechas de seguridad. Elige una diferente, más larga y combina letras, números y símbolos.';
      } else if (low.includes('should be at least') || low.includes('at least')) {
        description = `La contraseña es demasiado corta. Debe tener al menos ${MIN_LENGTH} caracteres.`;
      } else if (low.includes('same as the old') || low.includes('different from the old')) {
        description = 'La nueva contraseña debe ser distinta a la anterior.';
      } else if (low.includes('rate limit')) {
        description = 'Demasiados intentos. Espera unos segundos e inténtalo de nuevo.';
      }
      toast({ title: 'No se pudo actualizar', description, variant: 'destructive' });
      return;
    }

    // The DB trigger clears requires_password_reset on auth.users password change.
    // Refresh the in-memory profile so ProtectedRoute lets us through.
    await refreshProfile();
    setIsLoading(false);

    toast({ title: '¡Contraseña actualizada!', description: 'Ya puedes continuar usando la plataforma.' });
    navigate('/dashboard', { replace: true });
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    await forceLocalSignOut();
    // Hard reload to /auth — guarantees AuthContext and any stale React state
    // are reset, even if Supabase's signOut never resolved cleanly.
    window.location.href = '/auth';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Brain className="h-8 w-8 text-primary mx-auto mb-2" />
          <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-600">
            <ShieldAlert className="h-3.5 w-3.5" />
            Cambio obligatorio
          </div>
          <CardTitle>Cambia tu contraseña</CardTitle>
          <CardDescription>
            Tu administrador asignó una contraseña temporal. Por seguridad, debes cambiarla antes de continuar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  autoComplete="new-password"
                  minLength={MIN_LENGTH}
                  required
                />
                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Mínimo {MIN_LENGTH} caracteres.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirmar nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`pl-10 pr-10 ${passwordsMismatch ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                  autoComplete="new-password"
                  minLength={MIN_LENGTH}
                  aria-invalid={passwordsMismatch}
                  required
                />
                <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordsMismatch && (
                <p className="text-xs text-destructive">Las contraseñas no coinciden.</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading || isSigningOut || passwordsMismatch}>
              {isLoading ? 'Actualizando...' : 'Actualizar contraseña'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={isSigningOut}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSigningOut ? 'Cerrando sesión...' : 'Cerrar sesión'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordResetPage;
